// No-auth model: learners never log in. There is no login system, no password,
// no session cookie — a learner types their own ID ONCE on the entry screen
// (`/`). That POSTs the id to mileage's lookup endpoint (body, never a query
// string) and gets back a short-lived signed `lookup_token`. That token —
// NOT the raw id — is what's kept in sessionStorage and sent on every later
// read (balance refresh on `/mileage`, ranking's own-row highlight), via the
// `x-lookup-token` header. No route or query string in this app ever
// contains a user id. See NOAUTH-REPORT.md for the full design and its
// honest limitation (this stops URL tampering/link leakage, not someone
// typing another person's id into the form).

const LOOKUP_TOKEN_STORAGE_KEY = 'learner_ui_lookup_token';
export const DEFAULT_SITE_CODE = 'LMS1';

export function getStoredLookupToken(): string | null {
  return sessionStorage.getItem(LOOKUP_TOKEN_STORAGE_KEY);
}

export function setStoredLookupToken(token: string): void {
  sessionStorage.setItem(LOOKUP_TOKEN_STORAGE_KEY, token);
}

export function clearStoredLookupToken(): void {
  sessionStorage.removeItem(LOOKUP_TOKEN_STORAGE_KEY);
}

export interface MileageBalance {
  user_id: string;
  site_code: string;
  accumulated_mileage: number;
  level: 'level1' | 'level2' | 'level3' | 'master';
  level_label: string;
}

export interface LookupResponse extends MileageBalance {
  lookup_token: string;
  expires_in: number;
}

export interface DailyAccrualEntry {
  date: string;
  amount: number;
  note: string | null;
}

export interface DailyAccrualResponse {
  user_id: string;
  site_code: string;
  user_nm: string | null;
  accumulated_mileage: number;
  level: string;
  level_label: string;
  daily: DailyAccrualEntry[];
}

export interface RankingTopEntry {
  rank: number;
  user_nm: string;
  mileage: number;
  is_me: boolean;
}

export interface RankingResponse {
  snapshot_date: string | null;
  site_code: string;
  total_users: number;
  my_rank: number | null;
  my_mileage: number | null;
  top: RankingTopEntry[];
}

class LookupNotFoundError extends Error {
  constructor() {
    super('LOOKUP_NOT_FOUND');
  }
}

class LookupTokenInvalidError extends Error {
  constructor() {
    super('LOOKUP_TOKEN_INVALID');
  }
}

export { LookupNotFoundError, LookupTokenInvalidError };

/**
 * One-time id lookup (POST /api/mileage/lookup, id in the body). On success
 * this is the only place a lookup_token is minted — callers must store it
 * (setStoredLookupToken) and use it for everything after.
 */
export async function lookupMileage(userId: string, siteCode: string = DEFAULT_SITE_CODE): Promise<LookupResponse> {
  const response = await fetch('/api/mileage/lookup', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ user_id: userId, site_code: siteCode }),
  });
  if (response.status === 404) {
    throw new LookupNotFoundError();
  }
  if (!response.ok) {
    throw new Error('MILEAGE_LOOKUP_FAILED');
  }
  return response.json() as Promise<LookupResponse>;
}

/**
 * Balance refresh for an already-looked-up learner (GET /api/mileage/me
 * with x-lookup-token). Never sends a user_id anywhere.
 */
export async function fetchMyBalance(lookupToken: string): Promise<MileageBalance> {
  const response = await fetch('/api/mileage/me', {
    headers: { 'x-lookup-token': lookupToken },
  });
  if (response.status === 401) {
    throw new LookupTokenInvalidError();
  }
  if (!response.ok) {
    throw new Error('MILEAGE_ME_FAILED');
  }
  return response.json() as Promise<MileageBalance>;
}

/**
 * Daily accrual history for an already-looked-up learner (GET
 * /api/mileage/daily with x-lookup-token). Same identity model as
 * fetchMyBalance — the token, never a user_id, identifies the caller. The
 * server returns `daily` already sorted newest-first.
 */
export async function fetchDailyAccrual(lookupToken: string): Promise<DailyAccrualResponse> {
  const response = await fetch('/api/mileage/daily', {
    headers: { 'x-lookup-token': lookupToken },
  });
  if (response.status === 401) {
    throw new LookupTokenInvalidError();
  }
  if (!response.ok) {
    throw new Error('MILEAGE_DAILY_FAILED');
  }
  return response.json() as Promise<DailyAccrualResponse>;
}

/**
 * Public ranking (GET /api/mileage/ranking?site_code=&limit=). Passing the
 * lookup_token via header lets mileage mark the caller's own row `is_me`
 * server-side, entirely from the token — this app never tells the server
 * which row is "me" via any parameter of its own.
 */
export async function fetchRanking(
  lookupToken: string | null,
  siteCode: string = DEFAULT_SITE_CODE,
  limit = 20
): Promise<RankingResponse> {
  const params = new URLSearchParams({ site_code: siteCode, limit: String(limit) });
  const response = await fetch(`/api/mileage/ranking?${params.toString()}`, {
    headers: lookupToken ? { 'x-lookup-token': lookupToken } : {},
  });
  if (!response.ok) {
    throw new Error('MILEAGE_RANKING_FAILED');
  }
  return response.json() as Promise<RankingResponse>;
}

/**
 * Fetch wrapper for the app's own backend (learner-ui server.js proxy paths,
 * e.g. /api/curation/...). No identity is attached — curation is fully
 * anonymous — so this is just a thin, explicit alias over fetch() to keep
 * call sites descriptive.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(path, init);
}
